/**
 * Menu unique « Services » — groupes fixes dérivés de config/services.json
 * (structure du catalogue maintenue dans le JSON, jamais dans l'admin).
 * (pages dédiées /services/type/:id → CTA vers /services?type=:id)
 */
import servicesConfig from '../config/services.json';
import interpretingConfig from '../config/interpreting.json';

const jsonNavGroups = servicesConfig.groups.map((g) => ({
  id: g.id,
  title: g.name, // fallback FR si pas de traduction i18n
  names: { fr: g.name, en: g.nameEn || '', ar: g.nameAr || '' },
  items: g.services.map((s) => ({
    key: s.slug,
    path: `/services/type/${s.slug}`,
    catalogType: s.slug,
    title: s.name, // fallback si aucune traduction i18n n'existe encore
    names: { fr: s.name, en: s.nameEn || '', ar: s.nameAr || '' },
  })),
}));

export const servicesNavGroups = [
  ...jsonNavGroups,
  // Interprétariat : enregistré dans config/interpreting.json —
  // les items servent de secours local ; l'API (DB) les remplace quand elle répond.
  {
    id: interpretingConfig.id,
    title: interpretingConfig.name,
    names: { fr: interpretingConfig.name, en: interpretingConfig.nameEn || '', ar: interpretingConfig.nameAr || '' },
    items: interpretingConfig.services.map((s) => ({
      key: s.slug,
      path: `/services/type/${s.slug}`,
      catalogType: 'interpreting',
      title: s.name,
      names: { fr: s.name, en: s.nameEn || '', ar: s.nameAr || '' },
    })),
  },
];

/** @deprecated — conservé pour filtres / pages existantes */
export const translationNavGroups = servicesNavGroups.filter((g) =>
  g.id === 'professional' || g.id === 'official' || g.id === 'documents'
);

export const translationNavItems = translationNavGroups.flatMap((g) => g.items);
export const translationNavCategories = translationNavItems;

export const interpretingNavGroups = servicesNavGroups.filter((g) => g.id === 'interpreting');

export const interpretingNavItems = interpretingNavGroups.flatMap((g) => g.items);

export const otherNavGroups = [
  {
    id: 'other',
    items: [
      { key: 'transcription', path: '/services/type/transcription', catalogType: 'transcription' },
      { key: 'voiceover', path: '/services/type/voiceover', catalogType: 'voiceover' },
    ],
  },
];

export const otherNavItems = otherNavGroups.flatMap((g) => g.items);

/**
 * Filtres catalogue par type
 */
export const translationTypeFilters = [
  {
    id: 'official',
    categories: ['civil', 'academic', 'criminal', 'driving', 'legalMedical', 'financial', 'addons'],
    excludeIds: ['academicScientific'],
  },
  {
    id: 'sworn',
    categories: ['civil', 'criminal', 'legalMedical', 'addons'],
    excludeIds: ['medicalCert', 'vaccination', 'apostilleDoc'],
  },
  {
    id: 'certified',
    categories: ['civil', 'academic', 'criminal', 'driving', 'legalMedical', 'financial', 'addons'],
    excludeIds: ['academicScientific', 'apostilleDoc'],
  },
  {
    id: 'legalization',
    productIds: ['signatureLegalization', 'apostilleDoc', 'extraCopy', 'reissue'],
  },
  {
    id: 'apostille',
    productIds: ['apostilleDoc', 'signatureLegalization'],
  },
  {
    id: 'professional',
    productIds: [
      'commercialTranslation',
      'technicalWebsite',
      'marketingAdvertising',
      'tourismGuide',
      'academicScientific',
      'kbis',
      'workContract',
      'workCertificate',
      'statutes',
      'saleAgreement',
      'taxAttestation',
      'sirene',
    ],
  },
  {
    id: 'legal',
    productIds: [
      'judgment',
      'attestation',
      'adoptionJudgment',
      'divorceJudgment',
      'will',
      'workContract',
      'statutes',
      'saleAgreement',
      'kbis',
    ],
  },
  {
    id: 'technical',
    productIds: ['technicalWebsite', 'commercialTranslation'],
  },
  {
    id: 'medical',
    productIds: ['medicalCert', 'vaccination'],
  },
  {
    id: 'financial',
    categories: ['financial'],
    productIds: [
      'taxAttestation',
      'invoice',
      'bankStatement',
      'taxNotice',
      'payslip',
      'employmentAttestation',
      'bankAttestation',
    ],
  },
  {
    id: 'commercial',
    productIds: [
      'commercialTranslation',
      'kbis',
      'workContract',
      'workCertificate',
      'statutes',
      'saleAgreement',
      'sirene',
    ],
  },
  {
    id: 'marketing',
    productIds: ['marketingAdvertising'],
  },
  {
    id: 'it',
    productIds: ['technicalWebsite'],
  },
  {
    id: 'scientific',
    productIds: ['academicScientific', 'diploma', 'transcript', 'transcriptUni'],
  },
  {
    id: 'digital',
    productIds: ['technicalWebsite', 'marketingAdvertising'],
  },
  {
    id: 'website',
    productIds: ['technicalWebsite'],
  },
  {
    id: 'audiovisual',
    productIds: ['marketingAdvertising', 'tourismGuide'],
  },
  {
    id: 'transcription',
    productIds: ['marketingAdvertising', 'tourismGuide'],
  },
  {
    id: 'voiceover',
    productIds: ['marketingAdvertising', 'tourismGuide'],
  },
  {
    id: 'tourism',
    productIds: ['tourismGuide'],
  },
  {
    id: 'luxury',
    productIds: ['marketingAdvertising'],
  },
  {
    id: 'interpreting',
    productIds: ['onlineInterpreter'],
  },
  { id: 'simultaneous', productIds: ['onlineInterpreter'] },
  { id: 'consecutive', productIds: ['onlineInterpreter'] },
  { id: 'liaison', productIds: ['onlineInterpreter'] },
  { id: 'judicial', productIds: ['onlineInterpreter'] },
  { id: 'phone', productIds: ['onlineInterpreter'] },
  { id: 'video', productIds: ['onlineInterpreter'] },
  {
    id: 'general',
    categories: ['civil', 'academic', 'criminal', 'driving'],
  },
  {
    id: 'specialized',
    categories: ['business', 'financial', 'legalMedical'],
    excludeIds: ['apostilleDoc'],
  },
  {
    id: 'personal',
    categories: ['civil'],
  },
  {
    id: 'administrative',
    categories: ['criminal', 'driving'],
  },
  {
    id: 'academic',
    categories: ['academic'],
  },
  {
    id: 'legalDocs',
    categories: ['legalMedical'],
    excludeIds: ['medicalCert', 'vaccination', 'apostilleDoc'],
  },
  {
    id: 'commercialDocs',
    categories: ['business'],
  },
  {
    id: 'medicalDocs',
    categories: ['legalMedical'],
    excludeIds: ['judgment', 'attestation', 'adoptionJudgment', 'divorceJudgment', 'will', 'apostilleDoc'],
  },
  { id: 'medicalInterpreting', productIds: ['onlineInterpreter'] },
  { id: 'businessInterpreting', productIds: ['onlineInterpreter'] },
  { id: 'remoteInterpreting', productIds: ['onlineInterpreter'] },
];

export function getProductsForTranslationType(typeId, products) {
  if (!typeId || typeId === 'all') return products;

  const filter = translationTypeFilters.find((f) => f.id === typeId);
  if (!filter) return products;

  const idSet = new Set(filter.productIds || []);
  const catSet = new Set(filter.categories || []);
  const exclude = new Set(filter.excludeIds || []);

  return products.filter((p) => {
    if (exclude.has(p.id)) return false;
    return idSet.has(p.id) || catSet.has(p.category);
  });
}

export const guideTypeIds = [
  'official',
  'sworn',
  'certified',
  'legalization',
  'apostille',
  'professional',
  'general',
  'specialized',
  'legal',
  'technical',
  'medical',
  'financial',
  'commercial',
  'marketing',
  'it',
  'scientific',
  'digital',
  'website',
  'audiovisual',
  'transcription',
  'voiceover',
  'tourism',
  'luxury',
  'personal',
  'administrative',
  'academic',
  'legalDocs',
  'commercialDocs',
  'medicalDocs',
  'interpreting',
  'simultaneous',
  'consecutive',
  'liaison',
  'judicial',
  'phone',
  'video',
  'medicalInterpreting',
  'businessInterpreting',
  'remoteInterpreting',
];

export const catalogTypeIds = translationTypeFilters.map((f) => f.id);

/** Services sur mesure : le prix dépend du projet, donc devis obligatoire. */
export const quoteOnlyTypeIds = [
  'interpreting',
  'simultaneous',
  'consecutive',
  'liaison',
  'judicial',
  'phone',
  'video',
  'audiovisual',
  'transcription',
  'voiceover',
  'medicalInterpreting',
  'businessInterpreting',
  'remoteInterpreting',
];

/** Visuel de bannière par type de service */
const serviceTypeImages = {
  official: '/images/hero-sworn-translation.jpg',
  sworn: '/images/hero-sworn-translation.jpg',
  certified: '/images/home-zigzag-1.jpg',
  legalization: '/images/services/certified-true-copy.jpg',
  apostille: '/images/services/apostille-services.jpg',
  professional: '/images/home-zigzag-2.jpg',
  general: '/images/home-zigzag-1.jpg',
  specialized: '/images/home-zigzag-2.jpg',
  legal: '/images/services/docs/doc-judgment.jpg',
  technical: '/images/services/docs/doc-invoice-utility.jpg',
  medical: '/images/services/docs/doc-birth.jpg',
  financial: '/images/services/docs/doc-invoice-utility.jpg',
  commercial: '/images/home-zigzag-2.jpg',
  marketing: '/images/home-zigzag-2.jpg',
  it: '/images/services/docs/doc-transcript.jpg',
  scientific: '/images/services/docs/doc-diploma.jpg',
  digital: '/images/home-zigzag-2.jpg',
  website: '/images/home-zigzag-2.jpg',
  audiovisual: '/images/home-zigzag-1.jpg',
  transcription: '/images/services/docs/doc-transcript.jpg',
  voiceover: '/images/home-zigzag-1.jpg',
  tourism: '/images/earth-map.jpg',
  luxury: '/images/services/docs/doc-marriage.jpg',
  personal: '/images/services/docs/doc-birth.jpg',
  administrative: '/images/services/docs/doc-criminal-record.jpg',
  academic: '/images/services/docs/doc-diploma.jpg',
  legalDocs: '/images/services/docs/doc-judgment.jpg',
  commercialDocs: '/images/home-zigzag-2.jpg',
  medicalDocs: '/images/services/docs/doc-birth.jpg',
  interpreting: '/images/interpreting-general.jpg',
  simultaneous: '/images/interpreting-simultaneous.jpg',
  consecutive: '/images/interpreting-consecutive.jpg',
  liaison: '/images/interpreting-liaison.jpg',
  judicial: '/images/interpreting-judicial.jpg',
  phone: '/images/interpreting-phone.jpg',
  video: '/images/interpreting-video.jpg',
  medicalInterpreting: '/images/interpreting-medical.jpg',
  businessInterpreting: '/images/interpreting-business.jpg',
  remoteInterpreting: '/images/interpreting-remote.jpg',
};

/** Images déclarées dans services.json + interpreting.json (prioritaires) */
const jsonServiceImages = new Map(
  [...servicesConfig.groups.flatMap((g) => g.services), ...interpretingConfig.services]
    .map((s) => [s.slug, s.image])
    .filter(([, img]) => img),
);

export function getServiceTypeImage(typeId) {
  return jsonServiceImages.get(typeId) || serviceTypeImages[typeId] || '/images/hero-sworn-translation.jpg';
}

export const defaultServicesNavSectionId = 'specialties';
